<template>
	<Multiselect
		v-bind="$attrs"
		ref="multiselect"
		v-model="multiselectValue"
		:classes="{
			container: 'multiselect multiselect__filter',
			options: `multiselect-options list-none m-0 ${maxHeight}`,
			clear: `multiselect-clear hidden`,
			groupOptions: 'multiselect-group-options list-none m-0',
			groupLabel: 'multiselect-group-label bg-transparent p-0',
		}"
		@change="onChange"
		@close="onClose"
	>
		<template #option="{ option }">
			<div class="flex w-full" :class="option.class">
				<div
					v-if="_castArray(multiselectValue).includes(option.value)"
					class="mr-2 flex items-center justify-center"
				>
					<IconDropdownTick class="text-gray-650" />
				</div>
				<div class="flex w-full justify-between">
					<div class="">
						{{ option.label }}
					</div>
					<CarbarBadge
						v-if="option.count"
						background-color="bg-gray-160"
						text-color="text-gray-650"
						rounded="rounded-lg"
					>
						{{ option.count }}
					</CarbarBadge>
				</div>
			</div>
		</template>

		<template #grouplabel="group">
			<div
				v-if="group.group.label !== ''"
				class="w-full border-b border-t border-gray-200 px-2 py-5 text-base-sm font-normal text-gray-600"
			>
				<span>{{ group.group.label }}</span>
			</div>
		</template>

		<template #singlelabel="{ value }">
			<div class="multiselect-single-label">
				<div class="flex w-full flex-col">
					<div>
						<div
							v-if="$attrs.placeholder"
							class="multiselect-single-label-placeholder text-xs"
						>
							{{ $attrs.placeholder }}
						</div>
					</div>
					<div class="truncate text-gray-650">
						{{ value.label }}
					</div>
				</div>
			</div>
		</template>

		<template #multiplelabel="{ values }">
			<div class="multiselect-multiple-label">
				<div class="flex w-full flex-col">
					<div>
						<div
							v-if="$attrs.placeholder"
							class="multiselect-multiple-label-placeholder text-xs"
						>
							{{ $attrs.placeholder }}
						</div>
					</div>
					<div v-if="values.length" class="truncate text-gray-650">
						{{ values.map((item) => item.label).join(', ') }}
					</div>
				</div>
			</div>
		</template>

		<!-- eslint-disable vue/no-unused-vars -->
		<template v-if="showApplyAndClear" #afterlist="filteredOptions">
			<!-- eslint-enable vue/no-unused-vars -->
			<div class="flex justify-between bg-gray-120 px-2 py-2">
				<OutlineButton
					:size="3"
					text-color="text-gray-650"
					:is-full-width="false"
					:min-width="`min-w-none`"
					@click="onClear"
				>
					{{ clearLabel }}
				</OutlineButton>
				<CarbarButton
					:size="3"
					:is-full-width="false"
					:background-color="whitelabel?.styles?.button?.bg"
					:min-width="`min-w-none`"
					@click="onApply"
				>
					{{ applyLabel }}
				</CarbarButton>
			</div>
		</template>
	</Multiselect>
</template>

<script setup>
import { ref, watch } from 'vue'
import Multiselect from '@vueform/multiselect'
import { castArray as _castArray } from 'lodash-es'
import CarbarButton from '@/components/buttons/CarbarButton.vue'
import OutlineButton from '@/components/buttons/OutlineButton.vue'
import CarbarBadge from '@/components/badges/CarbarBadge.vue'
import IconDropdownTick from '@/assets/svg/common/ico-dropdown-tick.svg'

const props = defineProps({
	modelValue: [String, Number, Array],
	fieldLabel: {
		type: String,
		default: '',
		required: false,
	},
	showApplyAndClear: {
		type: Boolean,
		default: true,
		required: false,
	},
	applyLabel: {
		type: String,
		default: 'Apply',
		required: false,
	},
	clearLabel: {
		type: String,
		default: 'Clear',
		required: false,
	},
	maxHeight: {
		type: String,
		default: 'max-h-80',
		required: false,
	},
})

const { whitelabel } = useWhitelabelsStyles()

const emit = defineEmits([
	'clear',
	'close',
	'apply',
	'change',
	'update:modelValue',
])

const multiselect = ref(null)

const multiselectValue = ref(computed(() => props.modelValue).value)

watch(
	() => props.modelValue,
	(newValue) => {
		multiselectValue.value = newValue
	}
)

const onChange = (value) => {
	emit('update:modelValue', value)
	emit('change', value)
}

const onClear = () => {
	multiselect.value.clear()

	multiselect.value.close()

	emit('clear')
}

const onApply = () => {
	multiselect.value.close()

	emit('apply', multiselectValue.value)
}

const onClose = () => {
	emit('close', multiselectValue.value)
}

const refreshLabels = () => {
	multiselect.value.refreshLabels()
}

defineExpose({ refreshLabels })
</script>
